import { ITaxon } from '../services/insecta/insecta.models';
type PropertyPath<T> = (obj: T) => string | boolean | number;

export function isNotEmpty(x: any): boolean {
  // Checks for both null/undefined and empty string
  if (x === null || x === undefined || (typeof x === 'string' && x.trim().length === 0)) {
    return false;
  }
  return !isEmpty(x);
}

export function isEmpty(x: any): boolean {
  return Array.isArray(x) ? x === undefined || x === null || x.length === 0 : x === undefined || x === null;
}

export function isNotStringEmpty(x: string): boolean {
  return !isStringEmpty(x);
}

export function isStringEmpty(x: any): boolean {
  if (x === null || x === undefined || `${x}`.length === 0) return true;
  if (isEmpty(x)) return true;
  return `${x}`.trim().length === 0;
}

const collator = new Intl.Collator(undefined, { sensitivity: 'accent' });
export function isStringEqual(a: string, b: string): boolean {
  return collator.compare(a, b) === 0;
}

export function clone(x: any): any {
  return JSON.parse(JSON.stringify(x));
}

export function toShortDate(value: any): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };
  return new Date(value).toLocaleString('en-US', options);
}

export function toLongDate(value: any): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short' // use 'long' for full timezone name
  };
  return new Date(value).toLocaleString('en-US', options);
}

export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  // Month is 0-based in JS Date
  return new Date(year, month - 1, day);
}

export function dateToIsoString(dt: Date): string {
  return dt.toISOString().split('T')[0];
}

export function isValidEmail(email: string): boolean {
  return isEmpty(email) ? true : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function getScientificName(taxon: ITaxon): string | null {
  return taxon?.names?.find((x) => x.nameClass === 'scientific name')?.nameText ?? null;
}

export function collectionComparator<T>(...properties: PropertyPath<T>[]): (a: T, b: T) => number {
  return (a: T, b: T) => {
    for (const prop of properties) {
      const valA = prop(a);
      const valB = prop(b);

      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        if (valA !== valB) return valA ? -1 : 1;
        continue;
      }

      if (typeof valA === 'number' && typeof valA === 'number') {
        const numA = valA as number;
        const numB = valB as number;
        if (numA > numB) return 1;
        if (numA < numB) return -1;
        continue;
      }

      const strA = (valA as string).toUpperCase();
      const strB = (valB as string).toUpperCase();
      if (strA > strB) return 1;
      if (strA < strB) return -1;
    }
    return 0;
  };
}

export function normalizeOperator(operator: string): string {
  if (isStringEqual(operator, 'dateIs')) return 'EQUALS';
  if (isStringEqual(operator, 'dateIsNot')) return 'NOTEQUALS';
  if (isStringEqual(operator, 'dateAfter')) return 'GREATERTHAN';
  if (isStringEqual(operator, 'dateBefore')) return 'LESSTHAN';
  if (isStringEqual(operator, 'lt')) return 'LESSTHAN';
  if (isStringEqual(operator, 'lte')) return 'LESSTHANEQUAL';
  if (isStringEqual(operator, 'gt')) return 'GREATERTHAN';
  if (isStringEqual(operator, 'gte')) return 'GREATERTHANEQUAL';
  return operator;
}

export function downloadXlsxDocument(blob: Blob, filename: string) {
  const excelBlob = new Blob([blob], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(excelBlob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filename}`;

  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

export function getItemDescriptionColumn(settings: { [key: string]: any }): string {
  if (settings['useCommonName'] && settings['useSiteCode']) {
    return `ItemDescription_CS`;
  } else if (settings['useCommonName'] && !settings['useSiteCode']) {
    return `ItemDescription_C`;
  } else if (!settings['useCommonName'] && settings['useSiteCode']) {
    return `ItemDescription_S`;
  }
  return `ItemDescription`;
}
